import React, { useContext } from 'react';
import Scroll from 'react-scroll';
import cn from 'classnames';
import ConfigContext from '../../../contexts/ConfigContext';
import usePageContent, { CmsRichText } from '../../../hooks/usePageContent';

// Components
import LogoList from '../content/LogoList';
import Meta from '../../shared/Meta';
import ServicesForm from '../ServicesForm';
import Triple from '../content/Triple';
import CtaImage from '../content/CtaImage';
import FeaturedTrucks from '../FeaturedTrucks';
import SiteRequirements from '../content/SiteRequirements';
import Testimonials from '../content/Testimonials';
import OfficeFaq from '../faqs/OfficeFaq';

// Misc
import styles from '../../../../stylesheets/public/layouts/AboutPage.module.postcss';
import tripleStyles from '../content/Triple.module.postcss';
import CDN_BASE from '../../../utils/cdn';

const featuredTrucks = { featured: true };

function OfficeParks() {
  const config = useContext(ConfigContext);
  const {
    vehicle_type, city_name, site_title,
  } = config;
  const vehicleType = vehicle_type.toLowerCase();
  const citySlug = city_name.toLowerCase();

  // CMS-driven copy. See Residential.jsx for the pattern. Seattle has no row
  // → falls through to hardcoded JSX. Austin has bespoke copy → renders CMS.
  const { content: cms } = usePageContent('services_office_parks');

  return (
    <div>
      <Meta
        title={cms?.meta?.title || `Food ${vehicleType}s for offices and workplaces in ${city_name}`}
        breadcrumbName='Services'
        description={cms?.meta?.description || `Bring rotating food ${vehicleType}s to your office park or campus in ${city_name}. Set up a one-time lunch or a recurring schedule for your team.`}
      />

      <div className='Section'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-office-food-truck-catering.webp`} alt='Food truck serving employees at an office park' noLazy isResponsive />
          <div className={styles.Cta_copy}>
            <h1>{cms?.hero?.headline || `Food ${vehicleType}s for your workplace`}</h1>
            <p>{cms?.hero?.subheadline || `Set up a one-time lunch or a recurring rotation. We build the schedule and coordinate with the ${vehicleType}s.`}</p>
            <p>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                {cms?.hero?.cta_label || 'Set up workplace service'}
              </Scroll.Link>
            </p>
          </div>
        </div>
      </div>

      <div className='Section'>
        <LogoList title='Companies we work with' />
      </div>

      <section className='Section'>
        <h2>{cms?.how_it_works?.heading || 'How it works'}</h2>
        <div className={tripleStyles.Triple_wrapper}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-scheduled-food-truck-service.webp`}
            isResponsive
            alt='Food truck schedule for an office'
            title={cms?.how_it_works?.card1_title || 'We build the rotation'}
            text={cms?.how_it_works?.card1_body || `Choose how often you want ${vehicleType}s on site. We build the rotation, coordinate with vendors, and keep the schedule current.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-food-truck-menu-email.webp`}
            isResponsive
            alt='Weekly menu email for employees'
            title={cms?.how_it_works?.card2_title || 'Your team knows what’s coming'}
            text={cms?.how_it_works?.card2_body || `Give employees one place to see upcoming ${vehicleType}s and menus.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-mobile-food-truck-service.webp`}
            isResponsive
            alt='Food truck serving office employees'
            title={cms?.how_it_works?.card3_title || 'One point of contact'}
            text={cms?.how_it_works?.card3_body || `Questions, schedule changes, and vendor coordination come through ${site_title}, not your office manager.`}
          />
        </div>
      </section>

      <FeaturedTrucks
        title={`Featured food ${vehicleType}s`}
        options={featuredTrucks}
      />

      <section className='Section--extraPadding'>
        <h2>{cms?.benefits?.heading || 'What to expect'}</h2>
        {/* Fallback cards answer the bible §22 checklist; the CMS keys keep their legacy names. */}
        <div className={tripleStyles.Triple_wrapper + ' ' + tripleStyles.Triple_wrapper__quad}>
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-employee-food-truck-perk.webp`}
            isResponsive
            alt='Food truck parked at an office'
            title={cms?.benefits?.retention_title || 'What we need from you'}
            text={cms?.benefits?.retention_body || `A place for the ${vehicleType} to park, a service window, and basic site-access details. We’ll work through the rest with you.`}
          />
          {/* Pricing per the client (4 Sep 2026); cancellations per Master Policies v1.0 §11. */}
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-workplace-food-truck.webp`}
            isResponsive
            alt='Employees ordering at a food truck'
            title={cms?.benefits?.productivity_title || 'What it costs'}
            text={cms?.benefits?.productivity_body || `Most workplaces have us run it. Hosted service is a monthly program fee we quote up front, and we handle the schedule, the ${vehicleType}s, and the day-of details. Prefer to run it yourself? Submit a recurring request through the catering form and pick your own ${vehicleType}s. There’s no program fee that way.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-team-food-truck-lunch.webp`}
            isResponsive
            alt='Team eating lunch from a food truck'
            title={cms?.benefits?.community_title || `If a ${vehicleType} cancels`}
            text={cms?.benefits?.community_body || `We look for another ${vehicleType} first. A ${vehicleType} that cancels late pays a fee, and your schedule updates the moment anything changes.`}
          />
          <Triple
            image={`${CDN_BASE}/photos/${citySlug}-local-food-truck-vendors.webp`}
            isResponsive
            alt='Local food truck vendor at an office'
            title={cms?.benefits?.local_title || 'One lunch or a standing rotation'}
            text={cms?.benefits?.local_body || 'Try a single lunch first, or set a weekly rotation from the start.'}
          />
        </div>
      </section>


      <SiteRequirements />

      <section className='Section--extraPadding'>
        <div className={cn(styles.Cta, styles.Cta__reversed)}>
          <CtaImage image={`${CDN_BASE}/photos/${citySlug}-food-truck-employee-benefit.webp`} alt='Employee meal subsidization program' isResponsive />
          <div className={styles.Cta_copy}>
            <h3>{cms?.subsidies?.heading || 'Subsidize meals for your team'}</h3>
            <CmsRichText
              html={cms?.subsidies?.body}
              fallback={<p>Want to pick up part of the tab? Set an amount per employee and we&#39;ll set it up.</p>}
            />
            <div>
              <Scroll.Link
                smooth
                to='form'
                className='Button Button--mobile-w100'
                offset={-50}
              >
                {cms?.subsidies?.cta_label || 'Ask about subsidies'}
              </Scroll.Link>
            </div>
          </div>
        </div>
      </section>

      <Testimonials title='What people are saying' />

      <section className='Section--extraPadding'>
        <OfficeFaq />
      </section>

      <Scroll.Element name='form'>
        <div className='GrayBg'>
          <section className='Section--extraPadding'>
            <h2>{cms?.form?.heading || 'Tell us about your workplace'}</h2>
            <ServicesForm formType='offices' />
          </section>
        </div>
      </Scroll.Element>
    </div>
  );
}

export default OfficeParks;
